import { useEffect, useState } from "react";
import useAxios from "./useAxios";
import { IndexName, defaultDisplayRange, defaultRange, isScore } from "./satelliteProducts";

/** Field-level summary of one index for one acquisition, from `satellite-data/field-timeseries/<id>/`. */
export interface SatelliteStatPoint {
  satellite_data_id: number;
  request_id: number;
  acquisition_date: string;
  mean: number | null;
  median: number | null;
  std: number | null;
  p10: number | null;
  p90: number | null;
  min: number | null;
  max: number | null;
  valid_pixels: number;
  field_pixels: number | null;
  /** Share of the field that was cloud free (0–1), when known. */
  coverage: number | null;
}

export type SatelliteSeries = Record<string, SatelliteStatPoint[]>;

export const validSeriesPoints = (points: SatelliteStatPoint[] | undefined) =>
  (points ?? [])
    .filter((point) => point.mean !== null && Number.isFinite(point.mean))
    .sort((a, b) => Date.parse(a.acquisition_date) - Date.parse(b.acquisition_date));

/**
 * Loads field-average index values over time for every index of a field.
 * Change `refreshKey` to refetch, e.g. when new observations have been loaded.
 */
export function useSatelliteTimeSeries(fieldId: number | null | undefined, refreshKey?: unknown) {
  const api = useAxios();
  const [loaded, setLoaded] = useState<{ fieldId: number | null; series: SatelliteSeries }>({
    fieldId: null, series: {},
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!fieldId || !Number.isFinite(fieldId)) {
      setLoaded({ fieldId: null, series: {} });
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get(`satellite-data/field-timeseries/${fieldId}/`)
      .then((res) => {
        if (!cancelled) setLoaded({ fieldId, series: res.data?.series ?? {} });
      })
      .catch((err) => {
        if (cancelled) return;
        setLoaded({ fieldId, series: {} });
        setError(err?.response?.status ? `HTTP ${err.response.status}` : "Request failed");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldId, refreshKey]);

  // Never hand out the previous field's series while the new one loads.
  const series = loaded.fieldId === fieldId ? loaded.series : EMPTY_SERIES;
  return { series, loading, error };
}

const EMPTY_SERIES: SatelliteSeries = {};

/**
 * Default colour range from the loaded series: the lowest "most of field" low end (p10)
 * and the highest high end (p90) over all observations. Falls back to the static default.
 */
export const seriesDisplayRange = (
  points: SatelliteStatPoint[] | undefined,
  name: IndexName,
): [number, number] => {
  const lows = (points ?? []).map((p) => p.p10).filter((v): v is number => v !== null && Number.isFinite(v));
  const highs = (points ?? []).map((p) => p.p90).filter((v): v is number => v !== null && Number.isFinite(v));
  if (!lows.length || !highs.length) return defaultDisplayRange(name);
  const [limitLow, limitHigh] = defaultRange(name);
  const step = isScore(name) ? 1 : 0.01;
  let low = Math.max(limitLow, Math.floor(Math.min(...lows) / step) * step);
  let high = Math.min(limitHigh, Math.ceil(Math.max(...highs) / step) * step);
  if (high - low < step) {
    high = Math.min(limitHigh, low + step);
    low = Math.max(limitLow, high - step);
  }
  // Round away float noise from the step arithmetic.
  return [Number(low.toFixed(2)), Number(high.toFixed(2))];
};
